import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ShopProvider } from "./contexts/ShopContext";
import { TrackingProvider } from "./contexts/TrackingContext";
import { I18nProvider } from "./contexts/I18nContext";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import KakaoButton from "./components/KakaoButton";
import HomePage from "./pages/HomePage";
import StorePage from "./pages/StorePage";
import GeneratedProductPage from "./pages/GeneratedProductPage";
import RegionalRentalPage from "./pages/RegionalRentalPage";
import ConsultationPage from "./pages/ConsultationPage";
import ComparePage from "./pages/ComparePage";
import SitemapPage from "./pages/SitemapPage";
import PolicyPage from "./pages/PolicyPage";
import TrendSeoPage from "./pages/TrendSeoPage";
import SmartRedirect from "./components/SmartRedirect";

// Bots get the server-rendered /rental/<지역>/<구>/<제품> local landing page;
// humans landing on the same URL are sent to the real product detail page.
function RentalProductRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/p/${slug}`} replace />;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err, info) { console.error("React ErrorBoundary:", err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "#c00" }}>페이지 로드 오류</h2>
          <pre style={{ textAlign: "left", maxWidth: 600, margin: "20px auto", background: "#f5f5f5", padding: 16, borderRadius: 8, overflow: "auto", fontSize: 12 }}>
            {this.state.error?.toString()}
            {"\n"}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 24px", background: "#0071e3", color: "white", border: "none", borderRadius: 8, cursor: "pointer" }}>
            새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// 랜딩 전용 클론: 로그인/장바구니/결제/관리자/B2B/커뮤니티 라우트는 홈으로 리다이렉트.
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ShopProvider>
        <TrackingProvider>
        <I18nProvider>
        <ScrollToTop />
        <ErrorBoundary>
        <div className="min-h-screen flex flex-col bg-white">
          <Navbar />
          <main className="flex-1 pt-11">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/store/:category" element={<StorePage />} />
              <Route path="/search" element={<Navigate to="/" replace />} />
              <Route path="/products" element={<Navigate to="/store" replace />} />
              {/* Legacy / 색인된 URL → 시·구 추출 후 지역 랜딩 또는 상세로 */}
              <Route path="/product/:source/:id" element={<SmartRedirect />} />
              <Route path="/product/*" element={<SmartRedirect />} />
              <Route path="/category/*" element={<SmartRedirect />} />
              <Route path="/copier/:id" element={<SmartRedirect />} />
              <Route path="/copier/*" element={<SmartRedirect />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/consultation" element={<ConsultationPage />} />
              <Route path="/inquiry" element={<Navigate to="/consultation?tab=rental" replace />} />
              <Route path="/rental" element={<RegionalRentalPage />} />
              <Route path="/rental/:region" element={<RegionalRentalPage />} />
              <Route path="/rental/:region/:district" element={<RegionalRentalPage />} />
              <Route path="/rental/:region/:district/:slug" element={<RentalProductRedirect />} />
              <Route path="/sitemap" element={<SitemapPage />} />
              <Route path="/p/:slug" element={<GeneratedProductPage />} />
              <Route path="/trend" element={<TrendSeoPage />} />
              <Route path="/trend/:slug" element={<TrendSeoPage />} />
              <Route path="/policy" element={<PolicyPage />} />
              {/* 랜딩 전용: 비활성 기능은 홈으로 */}
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/signup" element={<Navigate to="/" replace />} />
              <Route path="/oauth/*" element={<Navigate to="/" replace />} />
              <Route path="/my" element={<Navigate to="/" replace />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/admin" element={<Navigate to="/" replace />} />
              <Route path="/cart" element={<Navigate to="/" replace />} />
              <Route path="/wishlist" element={<Navigate to="/" replace />} />
              <Route path="/checkout" element={<Navigate to="/" replace />} />
              <Route path="/order-confirm" element={<Navigate to="/" replace />} />
              <Route path="/payment-result" element={<Navigate to="/" replace />} />
              <Route path="/b2b" element={<Navigate to="/" replace />} />
              <Route path="/b2b/store" element={<Navigate to="/" replace />} />
              <Route path="/community" element={<Navigate to="/" replace />} />
              <Route path="/community/:slug" element={<Navigate to="/" replace />} />
              <Route path="/ai-tools" element={<Navigate to="/" replace />} />
              <Route path="/agent" element={<Navigate to="/" replace />} />
              {/* Catch-all 404: smart redirect (시/구 추출 → 지역렌탈, 아니면 홈) */}
              <Route path="*" element={<SmartRedirect />} />
            </Routes>
          </main>
          <Footer />
          <KakaoButton />
        </div>
        </ErrorBoundary>
        </I18nProvider>
        </TrackingProvider>
        </ShopProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
